import { motion } from "framer-motion";
import { ArrowUpRight, Play, Star } from "lucide-react";
import { MaskLine, EASE } from "@/lib/motion";
import { HERO_IMAGE } from "@/lib/site-data";
import { scrollToSection } from "@/lib/scroll";

const HEADLINE = ["Modern Websites", "Built to Grow", "Your Business"];

const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid" aria-hidden />
      <div
        className="glow-orb h-[420px] w-[420px] animate-pulse-glow"
        style={{ top: "-6rem", left: "-4rem", background: "#22d3ee" }}
        aria-hidden
      />
      <div
        className="glow-orb h-[460px] w-[460px] animate-pulse-glow"
        style={{ bottom: "-8rem", right: "-6rem", background: "#a855f7", animationDelay: "2s" }}
        aria-hidden
      />

      <div className="relative z-10 mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-6 md:px-12 lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left: copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-2"
            data-testid="hero-badge"
          >
            <span className="flex h-2 w-2 items-center justify-center">
              <span className="absolute h-2 w-2 animate-ping rounded-full bg-neon-cyan/60" />
              <span className="h-2 w-2 rounded-full bg-neon-cyan" />
            </span>
            <span className="text-xs font-medium text-white/70">
              Web design & development studio for small business
            </span>
          </motion.div>

          <h1 className="font-heading text-[2.7rem] font-semibold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[4.4rem]">
            {HEADLINE.map((line, i) => (
              <MaskLine key={line} delay={0.25 + i * 0.12}>
                {i === 2 ? <span className="text-gradient">{line}</span> : line}
              </MaskLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/60 md:text-lg"
            data-testid="hero-subtitle"
          >
            Yas Solutions crafts fast, responsive, professional websites that help
            businesses attract customers and build lasting credibility — designed
            around your goals, not a template.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <button
              onClick={() => scrollToSection("contact")}
              data-testid="hero-quote-button"
              className="group flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.03]"
            >
              Get a Free Quote
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollToSection("portfolio")}
              data-testid="hero-work-button"
              className="group flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
            >
              <Play className="h-4 w-4 fill-current" />
              Industries We Serve
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex items-center gap-5"
            data-testid="hero-social-proof"
          >
            <div className="flex -space-x-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-9 w-9 rounded-full border-2 border-ink bg-gradient-to-br from-neon-cyan/70 to-neon-purple/70"
                />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-neon-cyan text-neon-cyan" />
                ))}
              </div>
              <p className="mt-0.5 text-xs text-white/50">
                Trusted by growing local businesses
              </p>
            </div>
          </motion.div>
        </div>

        {/* Right: floating browser mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40, rotateX: 8 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.5 }}
          className="relative"
          style={{ perspective: 1200 }}
          data-testid="hero-mockup"
        >
          <div className="animate-float gradient-ring rounded-3xl">
            <div className="surface-card overflow-hidden rounded-3xl">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.03] px-4 py-3">
                <span className="h-3 w-3 rounded-full bg-white/20" />
                <span className="h-3 w-3 rounded-full bg-white/20" />
                <span className="h-3 w-3 rounded-full bg-white/20" />
                <div className="ml-3 flex-1 rounded-md bg-white/5 px-3 py-1 text-[10px] text-white/40">
                  yassolutions.ca
                </div>
              </div>
              <div className="relative">
                <img
                  src={HERO_IMAGE}
                  alt="Modern website design preview"
                  className="h-[300px] w-full object-cover md:h-[360px]"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              </div>
            </div>
          </div>

          {/* Floating stat card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 1.1 }}
            className="glass absolute -bottom-6 -left-4 rounded-2xl px-5 py-4 md:-left-8"
          >
            <p className="font-heading text-2xl font-semibold text-white">99+</p>
            <p className="text-xs text-white/50">PageSpeed score</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 1.25 }}
            className="glass absolute -top-5 -right-3 rounded-2xl px-5 py-4 md:-right-6"
          >
            <p className="font-heading text-2xl font-semibold text-gradient">100%</p>
            <p className="text-xs text-white/50">Responsive</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
